/**
 * Preditiva Gult Premium V2
 * Copyright (c) 2026 Preditiva. Todos os direitos reservados.
 * Tema proprietario - uso exclusivo clientes Preditiva.
 */
/**
 * global.js — Web Components base e utilitários globais
 * Web Components base e utilitários globais do tema Gult Premium.
 * Custom Elements definidos aqui ficam disponíveis em todo o tema.
 */

/* ========================================================================
   Utilitários
   ======================================================================== */

function getFocusableElements(container) {
  if (!container) return [];
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled"
    )
  );
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = Date.now();
    if (now - lastCall < delay) return;
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/* ========================================================================
   Trap Focus — Acessibilidade para modais e drawers
   ======================================================================== */

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = () => {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = (event) => {
    if (event.code.toUpperCase() !== 'TAB') return;

    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);
  elementToFocus.focus();
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);
  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;
  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;
  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

/* ========================================================================
   HTMLUpdateUtility — Atualização de DOM com deduplicação de IDs
    usado pelo Section Rendering API
   ======================================================================== */

class HTMLUpdateUtility {
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // Deduplica IDs para evitar conflitos durante a transição
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));
    setTimeout(() => oldNode.remove(), 500);
  }

  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

/* ========================================================================
   Custom Element: <quantity-input>
   Controle +/- de quantidade com validação de min/max
   ======================================================================== */

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange() {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;
    const button = event.currentTarget;

    if (button.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) {
      this.input.dispatchEvent(this.changeEvent);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      if (buttonMinus) {
        buttonMinus.classList.toggle('disabled', value <= parseInt(this.input.min));
      }
    }
    if (this.input.max) {
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      if (buttonPlus) {
        buttonPlus.classList.toggle('disabled', value >= parseInt(this.input.max));
      }
    }
  }
}

customElements.define('quantity-input', QuantityInput);

/* ========================================================================
   Custom Element: <sticky-header>
   Header que fica fixo ao fazer scroll
   ======================================================================== */

class StickyHeader extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('.section-header-global');
    this.headerIsAlwaysSticky = this.getAttribute('data-sticky-type') === 'always';
    this.headerBounds = {};
    this.currentScrollTop = 0;

    this.onScrollHandler = throttle(this.onScroll.bind(this), 50);

    if (this.headerIsAlwaysSticky) {
      this.header.classList.add('shopify-section-header-sticky');
    }

    this.createObserver();
    window.addEventListener('scroll', this.onScrollHandler, false);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
    this.observer?.disconnect();
  }

  createObserver() {
    this.observer = new IntersectionObserver((entries) => {
      this.headerBounds = entries[0].intersectionRect;
      this.stickyHeaderIsVisible = entries[0].isIntersecting;
    });
    this.observer.observe(this.header);
  }

  onScroll() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;

    if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      requestAnimationFrame(this.hide.bind(this));
    } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      requestAnimationFrame(this.reveal.bind(this));
    } else if (scrollTop <= this.headerBounds.top) {
      requestAnimationFrame(this.reset.bind(this));
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
    this.closeMenuDisclosure();
  }

  reveal() {
    this.header.classList.add('shopify-section-header-sticky', 'animate');
    this.header.classList.remove('shopify-section-header-hidden');
  }

  reset() {
    this.header.classList.remove(
      'shopify-section-header-hidden',
      'shopify-section-header-sticky',
      'animate'
    );
  }

  closeMenuDisclosure() {
    this.querySelectorAll('details[open]').forEach((disclosure) => {
      disclosure.removeAttribute('open');
    });
  }
}

customElements.define('sticky-header', StickyHeader);

/* ========================================================================
   Custom Element: <cart-drawer-items>
   Gerencia atualizações de quantidade no carrinho via AJAX
   ======================================================================== */

class CartDrawerItems extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', debounce(this.onQuantityChange.bind(this), 300));
  }

  async onQuantityChange(event) {
    const input = event.target;
    if (!input.matches('.quantity__input')) return;

    const index = input.dataset.index;
    const quantity = parseInt(input.value);
    const itemElement = input.closest('.cart-drawer__item');

    // Se quantidade for 0, animar remoção
    if (quantity === 0 && itemElement) {
      // Guardar altura para animar max-height
      itemElement.style.maxHeight = itemElement.offsetHeight + 'px';
      // Forçar reflow
      itemElement.offsetHeight;
      itemElement.classList.add('is-removing');
    }

    try {
      const cartDrawer = this.closest('cart-drawer');
      const sections = cartDrawer ? cartDrawer.getSectionsToRender().map(s => s.section) : [];

      const response = await fetch(`${window.routes?.cart_change_url || '/cart/change.js'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: parseInt(index),
          quantity: quantity,
          sections: sections,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Reverter animação se houve erro
        if (itemElement) {
          itemElement.classList.remove('is-removing');
          itemElement.style.maxHeight = '';
        }
        console.error('[cart-drawer-items] Erro ao atualizar carrinho:', data.description);
        return;
      }

      // Atualizar o drawer via Section Rendering API
      if (cartDrawer && data.sections) {
        cartDrawer.getSectionsToRender().forEach((section) => {
          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);

          if (sectionElement && data.sections[section.section]) {
            const html = new DOMParser()
              .parseFromString(data.sections[section.section], 'text/html')
              .querySelector(section.selector)?.innerHTML || '';

            // Aguardar animação de remoção antes de atualizar o DOM
            if (quantity === 0) {
              setTimeout(() => {
                sectionElement.innerHTML = html;
              }, 400);
            } else {
              sectionElement.innerHTML = html;
            }
          }
        });
      }

      // Se o carrinho ficou vazio, atualizar a classe
      if (data.item_count === 0) {
        const drawer = this.closest('cart-drawer');
        if (drawer) {
          setTimeout(() => {
            drawer.classList.add('is-empty');
          }, 400);
        }
      }

      // Publicar evento de atualização
      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-items',
          cartData: data,
        });
      }

    } catch (error) {
      console.error('[cart-drawer-items] Erro:', error);
      // Reverter animação em caso de erro
      if (itemElement) {
        itemElement.classList.remove('is-removing');
        itemElement.style.maxHeight = '';
      }
    }
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* ========================================================================
   Custom Element: <cart-drawer>
   Drawer lateral do carrinho com AJAX + Section Rendering API
   ======================================================================== */

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.close();
    });

    // Delegação de evento: o conteúdo do drawer é substituído pela Section
    // Rendering API a cada atualização do carrinho. Se o listener ficasse preso
    // ao nó do overlay, ele morreria na primeira atualização e o clique fora
    // deixaria de fechar o drawer.
    this.addEventListener('click', (event) => {
      if (event.target.closest('.cart-drawer__overlay')) this.close();
    });

    this.setHeaderCartIconAccessibility();

    // Listen for programmatic cart:open events (e.g., from sticky ATC button)
    document.addEventListener('cart:open', () => this.open());
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"]');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('open')) {
      /* no-op */
    }

    this.classList.add('is-open');
    this.querySelector('.drawer__inner')?.focus();

    document.body.classList.add('overflow-hidden');
    trapFocus(this.querySelector('.drawer__inner'));
  }

  close() {
    this.classList.remove('is-open');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      if (!parsedState.sections || !parsedState.sections[section.section]) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (sectionElement) {
        sectionElement.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );
      }
    });

    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '#cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML || '';
  }
}

customElements.define('cart-drawer', CartDrawer);

/* ========================================================================
   Accessibility: Details/Summary ARIA
    adiciona role="button" e aria-expanded em <summary>
   ======================================================================== */

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling?.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute(
      'aria-expanded',
      !event.currentTarget.closest('details').hasAttribute('open')
    );
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

/* ========================================================================
   Skip to Content Focus
   ======================================================================== */

document.querySelector('.skip-to-content')?.addEventListener('click', (event) => {
  event.preventDefault();
  const target = document.querySelector(event.currentTarget.getAttribute('href'));
  if (target) {
    target.focus();
    target.scrollIntoView({ behavior: 'smooth' });
  }
});

/* ========================================================================
   Mobile Menu Controller
   ======================================================================== */
document.addEventListener('DOMContentLoaded', () => {
  const toggleBtn = document.querySelector('.header__menu-toggle');
  const mobileMenu = document.getElementById('MobileMenu');
  const closeBtns = document.querySelectorAll('[data-close-menu]');

  if (toggleBtn && mobileMenu) {
    toggleBtn.addEventListener('click', () => {
      mobileMenu.hidden = false;
      document.body.classList.add('overflow-hidden');
    });
  }

  if (closeBtns.length > 0 && mobileMenu) {
    closeBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        mobileMenu.hidden = true;
        document.body.classList.remove('overflow-hidden');
      });
    });
  }
});

/* ========================================================================
   GP2 — Verificação de licença (Preditiva Gult Premium V2)
   Assinatura ECDSA P-256 verificada offline. Nenhuma requisição de rede,
   nenhum cookie, nenhum dado do visitante é tratado.

   INVARIANTES (não altere sem entender as consequências):
   1. crypto.subtle.verify() decide ANTES de qualquer outra checagem. kid
      desconhecido, versão futura ou flags reservadas são INVÁLIDAS — jamais
      um estado silencioso. Caso contrário qualquer pessoa monta uma string
      que não é assinada por ninguém e mesmo assim não sofre degradação.
   2. O estado silencioso ('desconhecido') só é alcançável por falha de
      AMBIENTE (sem Web Crypto, contexto inseguro, exceção, relógio ausente,
      chave pública não configurada no build). Nunca por conteúdo digitado
      pelo lojista.
   3. A expiração é comparada só contra o relógio do SERVIDOR (data-t).
      O relógio do visitante nunca entra na decisão.
   4. Nenhuma degradação no editor de temas nem em loja protegida por senha.
   5. Teto da degradação: um selo de crédito estático no rodapé, e só depois
      de 14 dias. Nunca overlay, nunca display:none global, nunca bloqueio.
   ======================================================================== */
(function () {
  'use strict';

  /* Chaves PÚBLICAS por kid. Expor chave pública é inofensivo — ela não
     gera assinatura. Substitua o placeholder pela saída de `node gp2.mjs init`.
     Enquanto o placeholder estiver aqui, o verificador fica em silêncio
     total (falha de build nunca pode punir a loja de um cliente). */
  var PUB = {
    1: 'BLzBeR3BTo_0yzc_P2cBjfG0PQ6pLNx3TV0UlNXYggr0hsZxotmd55Pw2XALTKhJCByNI8_7fpR3MnfRp_8oHnQ'
  };

  var CARENCIA_DIAS = 14;
  var LS_ANCORA = 'gp2.f0';
  var SELO_TXT = 'Tema Gult Premium — licença não verificada · preditiva.com.br';

  var OK = { ok: 1, ok_expirando: 1, exp_carencia: 1 };
  var SILENCIO = { desconhecido: 1 };

  function b64uDec(s) {
    s = String(s).replace(/-/g, '+').replace(/_/g, '/');
    while (s.length % 4) s += '=';
    var bin = atob(s);
    var out = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  }

  function bytesIguais(a, b, n) {
    var dif = 0;
    for (var i = 0; i < n; i++) dif |= (a[i] ^ b[i]);
    return dif === 0;
  }

  function u32(b, i) {
    return ((b[i] << 24) >>> 0) + (b[i + 1] << 16) + (b[i + 2] << 8) + b[i + 3];
  }

  function sha256(texto) {
    return crypto.subtle.digest('SHA-256', new TextEncoder().encode(texto))
      .then(function (buf) { return new Uint8Array(buf); });
  }

  function importar(pubB64) {
    return crypto.subtle.importKey(
      'raw', b64uDec(pubB64), { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify']
    );
  }

  /* Verifica UM token. Retorna sempre um estado — nunca lança. */
  function checarToken(token, ctx) {
    var partes = token.split('.');
    var claimsB64 = partes[1];
    var claims, assinatura;

    try {
      claims = b64uDec(claimsB64);
      assinatura = b64uDec(partes[2]);
    } catch (e) {
      return Promise.resolve({ st: 'invalida' });
    }
    if (claims.length !== 21 || assinatura.length !== 64) {
      return Promise.resolve({ st: 'invalida' });
    }

    var pub = PUB[claims[1]];
    // kid desconhecido: INVÁLIDA. Nunca silêncio (invariante 1).
    if (!pub) return Promise.resolve({ st: 'invalida' });

    return importar(pub)
      .then(function (chave) {
        return crypto.subtle.verify(
          { name: 'ECDSA', hash: 'SHA-256' },
          chave,
          assinatura,
          new TextEncoder().encode('GP2v1|' + claimsB64)
        );
      })
      .then(function (assinaturaOk) {
        if (!assinaturaOk) return { st: 'invalida' };

        // Só DEPOIS da assinatura conferida (invariante 1):
        if (claims[0] !== 1) return { st: 'invalida' };
        if (claims[2] & 0xFC) return { st: 'invalida' };

        var flags = claims[2];
        var exp = u32(claims, 3);
        var serial = u32(claims, 7);

        // Chave de homologação só vale em loja fechada por senha.
        if ((flags & 1) && ctx.p !== '1') return { st: 'invalida', serial: serial };

        return sha256('GP2dom|' + ctx.d).then(function (hash) {
          if (!bytesIguais(hash, claims.subarray(11, 21), 10)) {
            return { st: 'outro_dominio', serial: serial };
          }

          var st = 'ok';
          if (!(flags & 2)) { // não perpétua
            var restante = exp - ctx.t;
            if (restante < 0) {
              st = (-restante <= 90 * 86400) ? 'exp_carencia' : 'expirada';
            } else if (restante <= 30 * 86400) {
              st = 'ok_expirando';
            }
          }
          return { st: st, serial: serial, exp: exp };
        });
      })
      .catch(function () { return { st: 'desconhecido' }; });
  }

  /* Absorção multi-chave: uma chave genuína PARA ESTA LOJA sempre vence.
     Impede o downgrade de "colar licença roubada + lixo" para um estado leve. */
  function melhorEstado(resultados) {
    var i, r;
    for (i = 0; i < resultados.length; i++) {
      r = resultados[i];
      if (OK[r.st]) return r;
    }
    for (i = 0; i < resultados.length; i++) {
      r = resultados[i];
      if (r.st === 'outro_dominio') return r;
    }
    for (i = 0; i < resultados.length; i++) {
      if (resultados[i].st === 'expirada') return resultados[i];
    }
    return resultados[0] || { st: 'invalida' };
  }

  function selo() {
    if (document.getElementById('gp2-c')) return;
    var host = document.createElement('div');
    host.id = 'gp2-c';
    var raiz = host.attachShadow ? host.attachShadow({ mode: 'closed' }) : null;
    var conteudo =
      '<div style="all:initial;display:block;box-sizing:border-box;width:100%;' +
      'padding:6px 12px;text-align:center;font:400 11px/1.4 system-ui,-apple-system,sans-serif;' +
      'color:#8a8a8a;background:#111;letter-spacing:.02em;">' + SELO_TXT + '</div>';
    if (raiz) { raiz.innerHTML = conteudo; } else { host.innerHTML = conteudo; }
    document.body.appendChild(host);

    if (window.MutationObserver) {
      var obs = new MutationObserver(function () {
        if (!document.getElementById('gp2-c')) {
          try { document.body.appendChild(host); } catch (e) { /* noop */ }
        }
      });
      obs.observe(document.body, { childList: true });
    }
  }

  function diagnosticoEditor(estado, ctx) {
    var msg = {
      ok: 'Licença ativa.',
      ok_expirando: 'Licença ativa, vencendo em breve — solicite a renovação.',
      exp_carencia: 'Licença vencida (em carência). Renove para manter o suporte.',
      expirada: 'Licença vencida.',
      ausente: 'Cole a chave de licença em Configurações do tema → Licenciamento.',
      invalida: 'Chave não reconhecida — copie os 119 caracteres inteiros, sem cortar.',
      outro_dominio: 'Esta chave foi emitida para outra loja. Solicite a reemissão.',
      contexto_ausente: 'Contexto de licença ausente (tema modificado).',
      desconhecido: 'Não foi possível verificar a licença neste navegador.'
    }[estado.st] || estado.st;

    console.info('[GP2] ' + msg + ' | loja: ' + ctx.d + (estado.serial ? ' | licença nº ' + estado.serial : ''));

    if (estado.st === 'ok') return;

    var barra = document.createElement('div');
    barra.setAttribute('style',
      'position:fixed;left:0;right:0;bottom:0;z-index:2147483000;padding:10px 14px;' +
      'font:600 12px/1.4 system-ui,-apple-system,sans-serif;text-align:center;' +
      'color:#111;background:#f5c451;');
    barra.textContent = 'Licença Gult Premium: ' + msg + '  ·  Loja: ' + ctx.d;
    document.body.appendChild(barra);
  }

  function aplicar(estado, ctx) {
    window.GP2 = { st: estado.st, serial: estado.serial || null, exp: estado.exp || null, dom: ctx.d };

    // Editor de temas: só diagnóstico, nunca degradação (invariante 4).
    if (ctx.e === '1' || (window.Shopify && window.Shopify.designMode)) {
      diagnosticoEditor(estado, ctx);
      return;
    }

    // Loja fechada por senha: ninguém vê a vitrine mesmo.
    if (ctx.p === '1') return;

    if (OK[estado.st] || SILENCIO[estado.st]) {
      try { localStorage.removeItem(LS_ANCORA); } catch (e) { /* noop */ }
      return;
    }

    // Estado ruim: conta a carência a partir do relógio do servidor.
    var primeira = null;
    try { primeira = parseInt(localStorage.getItem(LS_ANCORA), 10); } catch (e) { /* noop */ }
    if (!primeira || isNaN(primeira) || primeira > ctx.t) {
      primeira = ctx.t;
      try { localStorage.setItem(LS_ANCORA, String(primeira)); } catch (e) { /* noop */ }
    }

    console.warn('[GP2] Licença não verificada (' + estado.st + ') — loja ' + ctx.d +
                 '. Regularize em preditiva.com.br para remover o selo de crédito.');

    if ((ctx.t - primeira) >= CARENCIA_DIAS * 86400) selo();
  }

  function iniciar() {
    var ctx;
    try {
      var el = document.getElementById('gp2');
      if (!el) { aplicar({ st: 'contexto_ausente' }, { d: '', t: 0, p: '0', e: '0' }); return; }

      ctx = {
        d: (el.getAttribute('data-d') || '').toLowerCase(),
        k: el.getAttribute('data-k') || '',
        t: parseInt(el.getAttribute('data-t'), 10),
        p: el.getAttribute('data-p'),
        e: el.getAttribute('data-e')
      };

      // Falhas de ambiente → silêncio total (invariante 2).
      if (!window.crypto || !crypto.subtle) return;
      if (!ctx.t || isNaN(ctx.t) || ctx.t < 1700000000) return;
      if (!PUB[1] || PUB[1].indexOf('COLE_AQUI') === 0) return;

      // Identidade cruzada: encarece fixar o domínio na mão.
      var shopJs = (window.Shopify && window.Shopify.shop) ? String(window.Shopify.shop).toLowerCase() : '';
      if (shopJs && ctx.d && shopJs !== ctx.d) {
        aplicar({ st: 'outro_dominio' }, ctx);
        return;
      }

      var bruto = ctx.k.replace(/\s+/g, '');
      if (!bruto) { aplicar({ st: 'ausente' }, ctx); return; }

      var tokens = bruto.match(/GP2\.[A-Za-z0-9_-]{28}\.[A-Za-z0-9_-]{86}/g);
      if (!tokens || !tokens.length) { aplicar({ st: 'invalida' }, ctx); return; }
      tokens = tokens.slice(0, 4);

      Promise.all(tokens.map(function (t) { return checarToken(t, ctx); }))
        .then(function (resultados) { aplicar(melhorEstado(resultados), ctx); })
        .catch(function () { /* silêncio: falha de ambiente */ });

    } catch (e) {
      /* Qualquer exceção inesperada → silêncio (invariante 2). */
    }
  }

  function agendar() {
    if (window.requestIdleCallback) { requestIdleCallback(iniciar, { timeout: 3000 }); }
    else { setTimeout(iniciar, 800); }
  }

  if (document.readyState === 'complete') agendar();
  else window.addEventListener('load', agendar);
})();
